import { useMemo } from 'react';

import { useGetSweetpassMembershipQuery } from '@order/graphql';
import { useFeatureFlag } from '@order/LaunchDarkly';

import { useCancelSweetpassMembership } from '../useCancelSweetpassMembership';
import { useReactivateSweetpassMembership } from '../useReactivateSweetpassMembership';

export const useSweetpassMembershipData = () => {
  const isSweetpassEnabled = useFeatureFlag('OPS-sweetpass-enabled', {
    listenForChanges: true,
  });

  const [response, refetchMembership] = useGetSweetpassMembershipQuery({
    pause: !isSweetpassEnabled,
    requestPolicy: 'cache-and-network',
  });

  // ─── Loyalty Profile Data ───────────────────────────────────────

  const {
    data: sweetpassMembershipData,
    error: loyaltyProfileError,
    fetching,
    stale,
  } = response;
  const isSweetpassMembershipLoading =
    fetching || (stale && !loyaltyProfileError);

  const loyaltyProfile = sweetpassMembershipData?.loyaltyProfile;
  const joinDate = loyaltyProfile?.joinDate;
  const tierStatus = loyaltyProfile?.tierStatus?.name;
  const tier = loyaltyProfile?.tier;
  const nextTier = loyaltyProfile?.tier?.nextTier;
  const hasPaidTierHistory = Boolean(loyaltyProfile?.hasPaidTierHistory);

  // ─── Subscriptions Data ─────────────────────────────────────────

  const subscriptions = sweetpassMembershipData?.getSubscriptions;
  const subscription =
    subscriptions && subscriptions.length > 0 ? subscriptions[0] : undefined;
  const subscriptionId = subscription?.id;
  const nextBillingDate = subscription?.nextBillingDate;

  // ─── Payment Methods Data ───────────────────────────────────────

  const paymentMethods =
    sweetpassMembershipData?.paymentSubscriptionProfile?.creditCards;

  // ─── Callbacks ──────────────────────────────────────────────────

  const { cancelSubscriptionAtCycleEnd } = useCancelSweetpassMembership({
    subscriptionId,
    nextBillingDate,
    tierName: tier?.name,
    refetchMembership,
  });

  const { reactivateSubscription } = useReactivateSweetpassMembership({
    subscriptionId,
    refetchMembership,
  });

  return useMemo(
    () => ({
      isSweetpassEnabled,
      isSweetpassMembershipLoading,
      tierStatus,
      tier: tier ? { id: tier.id, name: tier.name } : undefined,
      nextTier: nextTier ? { id: nextTier.id, name: nextTier.name } : undefined,
      hasPaidTierHistory,
      joinDate,
      activeSubscription: subscription
        ? {
            id: subscription.id,
            paidThroughDate: subscription.paidThroughDate as string | null,
            nextBillingDate: subscription.nextBillingDate,
            nextBillingPeriodAmount: subscription.nextBillingPeriodAmount,
          }
        : undefined,
      paymentMethods,
      loyaltyProfileError,
      cancelSubscriptionAtCycleEnd,
      reactivateSubscription,
      refetchMembership,
    }),
    [
      isSweetpassEnabled,
      isSweetpassMembershipLoading,
      tierStatus,
      tier,
      nextTier,
      hasPaidTierHistory,
      joinDate,
      subscription,
      paymentMethods,
      loyaltyProfileError,
      cancelSubscriptionAtCycleEnd,
      reactivateSubscription,
      refetchMembership,
    ],
  );
};
